import {useMemo, useEffect, useCallback, useState} from 'react'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useToast} from '@/hooks/useToast'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {getLearningCenterConsent} from '~/app/utils/helpers'
import {useIntl} from 'react-intl'
import {
    getGuestLearningData,
    saveGuestLearningData,
    updateLearningCenterOnServer
} from '../utils/helpers'
import {clearCompletedLessons, clearVisitedLessons} from 'hec/utils/guest-lesson-tracker'
type QuizAttempt = {
    pass: 0 | 1 // 0 - fail, 1 - pass
    time: string // Date ISO format
}

type Quiz = {
    id: string
    complete?: string // Date ISO format
    attempts?: QuizAttempt[]
}

type Lesson = {
    id: string
    complete?: string // Date ISO format
}

type Module = {
    id: string
    start?: string // Date ISO format
    complete?: string // Date ISO format
    lessons?: Lesson[]
    quiz?: Quiz
}

type Level = {
    id: string
    start?: string // Date ISO format
    complete?: string // Date ISO format
    modules: Module[]
}

type Goal = {
    id: string
    open?: string // Date ISO format
    start?: string // Date ISO format
    complete?: string // Date ISO format
    levels: Level[]
}

type LearningCenterData = {
    goals: Goal[]
    activeGoal: string // string of the active goal ID
    tpaLearningCenterConsentData: boolean | null
    activeLessonUrl?: string
} | null

type UpdatedDataResponse = {
    success: boolean
    data?: string
    errorMsg?: string
    errorDetail?: unknown
}

const mergeLearningCenterData = (
    registeredData: LearningCenterData,
    guestData: LearningCenterData,
    tpaLearningCenterConsentData: boolean | null
): LearningCenterData => {
    if (!guestData) return registeredData
    if (!registeredData) return guestData

    // Deep clone to avoid mutation
    const merged: LearningCenterData = JSON.parse(JSON.stringify(registeredData))

    guestData.goals.forEach((guestGoal) => {
        // Find or add goal
        const existingGoal = merged.goals.find((g) => g.id === guestGoal.id)
        if (!existingGoal) {
            merged.goals.push(guestGoal)
            return
        }

        guestGoal.levels.forEach((guestLevel) => {
            // Find or add level
            const existingLevel = existingGoal.levels.find((level) => level.id === guestLevel.id)
            if (!existingLevel) {
                existingGoal.levels.push(guestLevel)
                return
            }

            guestLevel.modules.forEach((guestModule) => {
                // Find or add module
                const existingModule = existingLevel.modules.find(
                    (module) => module.id === guestModule.id
                )
                if (!existingModule) {
                    existingLevel.modules.push(guestModule)
                    return
                }

                // Replace lessons if guest has more
                const guestLessonCount = guestModule.lessons?.length ?? 0
                const existingLessonCount = existingModule.lessons?.length ?? 0
                if (guestLessonCount > existingLessonCount) {
                    existingModule.lessons = guestModule.lessons
                }
            })
        })
    })

    return {
        activeGoal: guestData.activeGoal || registeredData.activeGoal,
        goals: merged.goals,
        tpaLearningCenterConsentData: tpaLearningCenterConsentData
    }
}

const useLearningCenter = () => {
    const {data: customer, isLoading, isSuccess, isError} = useCurrentCustomer()
    const {isRegistered, customerId, c_learningCenterData, customerType} = customer
    const isGuest = customerType === 'guest'
    const [guestLearningCenterData, setGuestLearningCenterData] = useState(() =>
        getGuestLearningData()
    )

    const learningCenterData = useMemo(() => {
        const sfccLearningCenterData = c_learningCenterData
        // pull data from local storage is user is not register
        if (isGuest && guestLearningCenterData) {
            return guestLearningCenterData
        }

        if (!sfccLearningCenterData) return {goals: []}

        try {
            return JSON.parse(sfccLearningCenterData)
        } catch (error) {
            throw new Error(
                `An error happened while parsing the SFCC Learning Center Data JSON. Error details: ${JSON.stringify(
                    error
                )}`
            )
        }
    }, [c_learningCenterData, isGuest, guestLearningCenterData])

    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')
    const toast = useToast()

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {formatMessage} = useIntl()

    const mergeGuestDataIntoProfile = useCallback(async () => {
        if (!isRegistered) return null
        if (isLoading || isError) return null

        const guestData = getGuestLearningData()
        const tpaLearningCenterConsentData = getLearningCenterConsent()
        const merged = mergeLearningCenterData(
            learningCenterData,
            guestData,
            tpaLearningCenterConsentData
        )

        return updateLearningCenterOnServer(
            customerId,
            merged,
            updateCustomerMutation,
            toast,
            formatMessage
        )
    }, [
        customerId,
        formatMessage,
        isError,
        isLoading,
        isRegistered,
        learningCenterData,
        toast,
        updateCustomerMutation
    ])

    useEffect(() => {
        const guestData = getGuestLearningData()

        // If user is registered and hasn't already merged guest data
        if (
            isRegistered &&
            isSuccess &&
            !guestData?.hasRegistered &&
            learningCenterData !== null &&
            guestData?.goals?.length > 0
        ) {
            // Prevent future duplicate merges
            const updatedData = {
                ...guestData,
                hasRegistered: true
            }

            saveGuestLearningData(updatedData)

            // Attempt to merge guest data into SFCC profile
            mergeGuestDataIntoProfile()
                .then((res) => {
                    console.log('Merged guest data into profile successfully', res)
                    clearCompletedLessons()
                    clearVisitedLessons()
                })
                .catch((err) => {
                    console.error('Failed to merge guest data into profile', err)
                })
        }
    }, [isRegistered, isSuccess, learningCenterData, mergeGuestDataIntoProfile])

    const updateData = (reqBody: LearningCenterData): Promise<UpdatedDataResponse> | null => {
        if (!isRegistered) {
            try {
                const newData = structuredClone(reqBody)
                saveGuestLearningData(newData)
                setGuestLearningCenterData(() => newData)
                return Promise.resolve({
                    success: true,
                    data: JSON.stringify(newData)
                })
            } catch (error) {
                return Promise.reject({
                    success: false,
                    errorMsg: 'Failed to save to localStorage',
                    errorDetail: error
                })
            }
        }
        if (reqBody) {
            reqBody.tpaLearningCenterConsentData = getLearningCenterConsent()
        }

        return updateLearningCenterOnServer(
            customerId,
            reqBody,
            updateCustomerMutation,
            toast,
            formatMessage
        )
    }

    const getGoalByID = (goalID: string, data?: LearningCenterData) => {
        const lcData: LearningCenterData = data || learningCenterData
        return lcData?.goals ? lcData.goals.find((goal) => goal.id === goalID) : null
    }

    const getActiveGoal = (data?: LearningCenterData) => {
        const lcData = data || learningCenterData
        if (!lcData?.activeGoal) return null

        const activeGoalID = lcData.activeGoal || lcData.id
        const activeGoal = getGoalByID(activeGoalID, lcData)

        return activeGoal || null
    }

    const getLevelByID = (levelID: string, data?: LearningCenterData) => {
        const lcData: LearningCenterData = data || learningCenterData
        if (!lcData) return null

        return (
            lcData.goals.flatMap((goal) => goal.levels).find((level) => level.id === levelID) ||
            null
        )
    }

    const getModuleByID = (moduleID: string, data?: LearningCenterData) => {
        const lcData: LearningCenterData = data || learningCenterData
        if (!lcData) return null

        return (
            lcData.goals
                .flatMap((goal) => goal.levels.flatMap((level) => level.modules))
                .find((module) => module.id === moduleID) || null
        )
    }

    const getLessonByID = (lessonID: string, data?: LearningCenterData) => {
        const lcData: LearningCenterData = data || learningCenterData
        if (!lcData) return null

        return (
            lcData.goals
                .flatMap((goal) =>
                    goal.levels.flatMap((level) =>
                        level.modules.flatMap((module) => module.lessons)
                    )
                )
                .find((lesson) => lesson?.id === lessonID) || null
        )
    }

    const getQuizByModuleID = (moduleID: string, data?: LearningCenterData) => {
        const lcData = data || learningCenterData
        if (!lcData) return null

        const module = getModuleByID(moduleID, lcData)
        return module?.quiz || null
    }

    /**
     * Updates only the tpaLearningCenterConsentData field for the current user
     * @param consent boolean value from modal
     * @returns Promise<UpdatedDataResponse> | null
     */
    const updateConsentData = (consent: boolean): Promise<UpdatedDataResponse> | null => {
        const reqBody = {
            ...learningCenterData,
            tpaLearningCenterConsentData: consent
        }
        return updateLearningCenterOnServer(
            customerId,
            reqBody,
            updateCustomerMutation,
            toast,
            formatMessage
        )
    }

    return {
        learningCenterData,
        updateData,
        updateConsentData,
        getGoalByID,
        getActiveGoal,
        getLevelByID,
        getModuleByID,
        getLessonByID,
        getQuizByModuleID
    }
}

export default useLearningCenter
